import { Link } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertList,
  ErrorState,
  ModuleFilters,
  PageHeader,
  SectionHeader
} from '../../../shared/components/index.js';
import { useModuleFilters } from '../../../shared/hooks/useModuleFilters.js';
import { loadExecutiveSummary } from '../services/executiveSummaryService.js';
import EconomicFlow from '../components/EconomicFlow.jsx';
import StatusBlock from '../components/StatusBlock.jsx';
import { cacheGet } from '../../../shared/utils/session-cache.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch (e) {
    return iso;
  }
}

function resumenCacheKey(dateFrom, dateTo) {
  return 'execSummary:' + String(dateFrom || '') + '|' + String(dateTo || '');
}

export default function ResumenEjecutivoPage({ email }) {
  const filters = useModuleFilters({ period: 'last_7_days', persistKey: 'resumen-ejecutivo' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const forceRef = useRef(false);
  const loadSeq = useRef(0);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!hasSearched) return;
      forceRef.current = true;
      setTick((t) => t + 1);
    }, [hasSearched])
  );

  useEffect(() => {
    if (!hasSearched) return;
    let cancelled = false;
    const seq = ++loadSeq.current;
    const force = forceRef.current;
    forceRef.current = false;

    const key = resumenCacheKey(filters.range.dateFrom, filters.range.dateTo);
    const hit = !force ? cacheGet(key) : null;
    if (hit && !cancelled) {
      setData(hit.data);
      setLoading(false);
      setError('');
      return undefined;
    }

    setLoading(true);
    setError('');
    (async () => {
      try {
        const payload = await loadExecutiveSummary(
          {
            dateFrom: filters.range.dateFrom,
            dateTo: filters.range.dateTo
          },
          { force }
        );
        if (cancelled || seq !== loadSeq.current) return;
        setData(payload);
        setLoading(false);
      } catch (err) {
        if (cancelled || seq !== loadSeq.current) return;
        setError((err && err.message) || 'No se pudo cargar el resumen ejecutivo.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  function runSearch() {
    setHasSearched(true);
    forceRef.current = true;
    setTick((t) => t + 1);
  }

  function cancelSearch() {
    loadSeq.current += 1;
    setLoading(false);
  }

  const alerts = (data && data.alerts) || [];

  return (
    <div className="analytics-module analytics-resumen-ejecutivo">
      <PageHeader
        eyebrow="Centro de Control · GI Capital"
        title="Resumen Ejecutivo"
        subtitle={
          (data && data.compareNote
            ? data.compareNote + ' '
            : 'Narrativa inversión → actividad → resultado. ') +
          (email ? `· ${email}` : '')
        }
        actions={
          <ModuleFilters
            filters={filters}
            disabled={false}
            busy={loading}
            onRefresh={runSearch}
            onCancel={cancelSearch}
            showExecutive={false}
            showProject={false}
            showChannel={false}
            showStatus={false}
          />
        }
      />

      {!hasSearched && !loading ? (
        <p className="analytics-empty">
          Elegí el período y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      <p className="exec-meta" aria-live="polite">
        Período {filters.range.dateFrom} → {filters.range.dateTo}
        {data && data.compareRange
          ? ` · Comparable ${data.compareRange.dateFrom} → ${data.compareRange.dateTo}`
          : ''}
        {' · '}
        Actualizado {formatWhen(data && data.generatedAt)}
        {data && data._fromCache ? ' · en caché' : ''}
        {loading && data ? ' · actualizando…' : ''}
      </p>

      <ErrorState message={error} onRetry={runSearch} />

      {loading && !data ? <p className="analytics-empty">Buscando resumen ejecutivo…</p> : null}

      {!loading && data ? (
        <>
          <StatusBlock status={data.status} />

          <section className="analytics-section">
            <SectionHeader
              title="Gastos del mes"
              note="Radar operativo (Meta, voz, nube, IA, SaaS). Detalle y carga de montos en el portal Admin."
            />
            <div className="analytics-panel exec-panel">
              <p>
                <strong>¿Cómo van los gastos este mes?</strong>
              </p>
              <p>
                El panel unificado vive en el portal (planilla espejo + Meta Ads
                en vivo). Desde acá abrís el detalle mes a la fecha, % del
                presupuesto y proyección.
              </p>
              <p className="exec-econ-hint">
                Dashboard Ejecutivo del portal redirige a este resumen; los
                costos se gestionan en Admin → Sistema → Costos del mes.
              </p>
              <p style={{ marginTop: 10 }}>
                <a href="https://portal.gicapital.cl/app.html#/costos">
                  Abrir Costos del mes →
                </a>
              </p>
            </div>
          </section>

          {data.webKommoHealth ? (
            <section className="analytics-section">
              <SectionHeader
                title="Formulario web → Kommo"
                note="Si el diagnóstico se guardó pero no llegó al CRM, acá lo ves. Detalle y carga en Fugas Meta."
              />
              <div
                className={
                  'analytics-panel exec-panel' +
                  (data.webKommoHealth.status === 'critical'
                    ? ' is-alert'
                    : data.webKommoHealth.status === 'attention'
                      ? ' is-warn'
                      : '')
                }
              >
                <p>
                  <strong>
                    {data.webKommoHealth.status === 'ok'
                      ? 'Llegando bien'
                      : data.webKommoHealth.status === 'critical'
                        ? 'Se están perdiendo leads'
                        : data.webKommoHealth.status === 'attention'
                          ? 'Hay leads sin Kommo'
                          : 'Sin dato'}
                  </strong>
                </p>
                <p>{data.webKommoHealth.message}</p>
                {data.webKommoHealth.ok ? (
                  <p className="exec-econ-hint">
                    Web {data.webKommoHealth.total} · Con Kommo {data.webKommoHealth.conKommo} · Sin
                    Kommo {data.webKommoHealth.sinKommo}
                    {data.webKommoHealth.waNoEntregado
                      ? ` · WA no entregado ${data.webKommoHealth.waNoEntregado}`
                      : ''}
                    {data.webKommoHealth.arrivalRate != null
                      ? ` · Llegada ${data.webKommoHealth.arrivalRate}%`
                      : ''}
                  </p>
                ) : null}
                <p style={{ marginTop: 10 }}>
                  <Link to="/fugas-meta">Abrir Fugas Meta →</Link>
                </p>
              </div>
            </section>
          ) : null}

          {data.proposalConversion && data.proposalConversion.available ? (
            <section className="analytics-section">
              <SectionHeader
                title="¿Las propuestas concretan?"
                note="Cohort real: propuestas del período con Reserva ID en la misma fila."
              />
              <div className="analytics-panel exec-panel exec-proposal-conv">
                <p className="exec-proposal-conv-rate">
                  {data.proposalConversion.rate}%
                  {data.proposalConversion.deltaPp != null ? (
                    <span
                      className={
                        'exec-flow-delta' +
                        (data.proposalConversion.deltaPp < 0
                          ? ' is-down'
                          : data.proposalConversion.deltaPp > 0
                            ? ' is-up'
                            : '')
                      }
                    >
                      {' '}
                      {data.proposalConversion.deltaPp > 0 ? '+' : ''}
                      {data.proposalConversion.deltaPp} pp vs comparable
                    </span>
                  ) : null}
                </p>
                <p>
                  <strong>{data.proposalConversion.converted}</strong> de{' '}
                  <strong>{data.proposalConversion.proposals}</strong> propuestas
                  concretaron en reserva
                  {data.proposalConversion.pending != null
                    ? ` · ${data.proposalConversion.pending} aún sin reserva`
                    : ''}
                  .
                </p>
                <p className="exec-econ-hint">{data.proposalConversion.definition}</p>
              </div>
            </section>
          ) : null}

          <section className="analytics-section">
            <SectionHeader
              title="Flujo económico"
              note="Solo etapas con dato trazable. El resto se marca No disponible."
            />
            <div className="analytics-panel exec-panel">
              <EconomicFlow stages={data.flow} conversions={data.conversions} />
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader
              title="Indicadores económicos"
              note="Sin inventar ROI ni comisiones."
            />
            <div className="exec-econ-grid">
              {(data.economics || []).map((e) => (
                <article
                  key={e.id}
                  className={
                    'exec-econ-card' + (e.status === 'unavailable' ? ' is-unavailable' : '')
                  }
                >
                  <h3>{e.label}</h3>
                  <p className="exec-econ-value">
                    {e.status === 'unavailable' || e.value == null
                      ? 'No disponible'
                      : typeof e.value === 'number'
                        ? e.value.toLocaleString('es-CL')
                        : String(e.value)}
                  </p>
                  {e.hint ? <p className="exec-econ-hint">{e.hint}</p> : null}
                </article>
              ))}
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader title="Principal punto de pérdida" />
            <div className="analytics-panel exec-panel">
              {data.bottleneck && data.bottleneck.available ? (
                <>
                  <h3 className="exec-bottleneck-label">{data.bottleneck.label}</h3>
                  <p>{data.bottleneck.message}</p>
                  {data.bottleneck.estimate ? (
                    <p className="exec-estimate">
                      <strong>Estimación:</strong> {data.bottleneck.estimate}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="analytics-empty">
                  {(data.bottleneck && data.bottleneck.message) ||
                    'Sin cuello de botella calculable en este período.'}
                </p>
              )}
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader
              title="Ritmo del mes"
              note="Requiere objetivos configurados."
            />
            <div className="analytics-panel exec-panel">
              <p className="analytics-empty">
                {(data.pace && data.pace.message) ||
                  'Objetivo no configurado. No se muestra proyección inventada.'}
              </p>
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader title="Lectura ejecutiva" note="Reglas determinísticas." />
            <div className="analytics-panel exec-panel">
              <ul className="exec-reading">
                {(data.reading || []).map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader title="Alertas accionables" />
            <div className="analytics-panel">
              <AlertList
                items={alerts.map((a) => ({
                  severity:
                    a.severity === 'critical'
                      ? 'bad'
                      : a.severity === 'info'
                        ? 'ok'
                        : 'warn',
                  title: a.title,
                  detail:
                    (a.detail || '') + (a.actionHint ? ' · ' + a.actionHint : '')
                }))}
                emptyLabel="Sin alertas priorizadas en este período."
              />
              {alerts.some((a) => a.href) ? (
                <p className="exec-alert-links">
                  {alerts
                    .filter((a) => a.href)
                    .map((a) => (
                      <Link key={a.title} to={a.href} className="exec-alert-link">
                        {a.title} →
                      </Link>
                    ))}
                </p>
              ) : null}
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader title="Cobertura de datos" />
            <div className="analytics-panel exec-panel">
              <p>
                Etapas con dato:{' '}
                <strong>
                  {(data.coverage && data.coverage.availableStages) || 0}/
                  {(data.coverage && data.coverage.totalStages) || 0}
                </strong>
              </p>
              <p className="exec-econ-hint">
                {(data.coverage && data.coverage.attributionNote) || ''}
              </p>
              {(data.coverage && data.coverage.warnings && data.coverage.warnings.length) ? (
                <ul className="exec-reading">
                  {data.coverage.warnings.slice(0, 6).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>

          <section className="analytics-section">
            <SectionHeader title="Explorar módulos" note="Profundidad por área." />
            <div className="analytics-module-grid">
              {(data.modules || []).map((m) => (
                <Link key={m.id} className="analytics-module-card is-live" to={m.path}>
                  <h3>{m.label}</h3>
                  <span className="analytics-module-card-cta">Abrir →</span>
                </Link>
              ))}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
